import React, { useState, useContext } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Drawer,
  Tooltip,
  useMediaQuery,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { AuthContext } from '../auth-context';


// 👉 UPDATE THIS PATH IF NEEDED
import logo from "../assets/MF-CPU-LOGO.png";

const SIDEBAR_WIDTH = 260;

const menuItems = [
  {
    label: "Dashboard",
    path: "/dashboard",
    icon: DashboardOutlinedIcon,
    description: "Main dashboard overview"
  },
  {
    label: "Order Dashboard",
    path: "/order-dashboard",
    icon: AssessmentOutlinedIcon,
    description: "View and manage orders"
  },
  {
    label: "Users",
    path: "/add-users",
    icon: PeopleAltOutlinedIcon,
    description: "Add new users"
  },
  {
    label: "Manage Users",
    path: "/manage-users",
    icon: GroupOutlinedIcon,
    description: "Manage existing users"
  },
  {
    label: "Inventory",
    path: "/inventory",
    icon: Inventory2OutlinedIcon,
    description: "Manage inventory items"
  },
  {
    label: "Invoices",
    path: "/invoices",
    icon: ReceiptLongOutlinedIcon,
    description: "View all invoices"
  },
  {
    label: "Console Invoices",
    path: "/ConsolidatedInvoiceScreen",
    icon: SummarizeOutlinedIcon,
    description: "Consolidated invoice view"
  },
  {
    label: "Waste Management",
    path: "/waste-management",
    icon: DeleteOutlineOutlinedIcon,
    description: "Track and manage waste"
  },
  {
    label: "CutOff Notifications",
    path: "/cutoff-notifications",
    icon: AssignmentLateOutlinedIcon,
    description: "Order cutoff notifications"
  },
];

const SidebarContent = ({ onClose }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  const handleLogout = () => {
    logout();
    navigate('/');
    if (onClose) onClose();
  };

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        backgroundColor: theme.palette.background.paper,
        display: "flex",
        flexDirection: "column",
        padding: "20px 14px",
      }}
    >
      {/* HEADER (LOGO + CLOSE ICON FOR MOBILE) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
        }}>
          <img
            src={logo}
            alt="MF Central Kitchen"
            style={{ width: '70%' }}
          />
        </Box>

        {onClose && (
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* MENU */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));
          const Icon = item.icon;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              style={{ textDecoration: "none" }}
              onClick={onClose}
            >
              <Tooltip title={item.description || item.label} placement="right" arrow>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    padding: "10px 14px",
                    borderRadius: theme.shape.borderRadius,
                    marginBottom: "6px",
                    cursor: "pointer",
                    backgroundColor: isActive
                      ? theme.palette.primary.light
                      : "transparent",
                    color: isActive
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    fontWeight: isActive ? 600 : 500,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 20,
                      color: isActive
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    }}
                  />
                  <Typography variant="body1" sx={{ fontSize: "14px" }}>
                    {item.label}
                  </Typography>

                  {/* Active indicator */}
                  {isActive && (
                    <Box
                      sx={{
                        width: 3,
                        height: 20,
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: 3,
                        ml: 'auto',
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            </NavLink>
          );
        })}
      </Box>

      {/* FOOTER */}
      <Box sx={{ mt: "auto", px: 1 }}>
        <Divider sx={{ mb: 1 }} />
        <Button
          fullWidth
          startIcon={<LogoutOutlinedIcon />}
          onClick={handleLogout}
          sx={{
            justifyContent: 'flex-start',
            color: theme.palette.error.main,
            textTransform: 'none',
            fontWeight: 500,
            py: 1,
            px: 1.5,
            borderRadius: theme.shape.borderRadius,
            mb: 1,
            '&:hover': {
              backgroundColor: theme.palette.error.light + '20',
            },
          }}
        >
          Logout
        </Button>
        <Typography
          sx={{
            fontSize: "11px",
            color: theme.palette.text.secondary,
            textAlign: 'center',
          }}
        >
          © {new Date().getFullYear()} Madras Flavours
        </Typography>
        <Typography
          sx={{
            fontSize: "10px",
            color: theme.palette.text.disabled,
            textAlign: 'center',
            mt: 0.5,
          }}
        >
          v1.0.0
        </Typography>
      </Box>
    </Box>
  );
};

const Sidebar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);

  if (isMobile) {
    return (
      <>
        {/* MOBILE MENU ICON */}
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            top: 11,
            left: 16,
            zIndex: 1300,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[2],
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* MOBILE DRAWER */}
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          PaperProps={{
            sx: {
              width: SIDEBAR_WIDTH,
              boxShadow: theme.shadows[8],
            },
          }}
        >
          <SidebarContent onClose={() => setOpen(false)} />
        </Drawer>
      </>
    );
  }

  /* DESKTOP SIDEBAR */
  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100vh",
        position: "sticky",
        top: 0,
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[1],
      }}
    >
      <SidebarContent />
    </Box>
  );
};

export default Sidebar;